import type { RopcTokenResponse } from '../services/ropc-auth.service';

function generateMockIdToken(userId: string, email: string, firstName: string, lastName: string): string {

  const header = {
    typ: 'JWT',
    alg: 'RS256',
    kid: 'mock-key-id',
  };

  const now = Math.floor(Date.now() / 1000);
  const payload = {
    iss: 'https://login-mock.loandepotdev.works/mock-tenant/v2.0/',
    sub: userId,
    aud: 'mock-client-id',
    exp: now + 3600, // 1 hour from now
    iat: now,
    nbf: now,
    oid: userId, // Object ID - used as userId
    given_name: firstName,
    family_name: lastName,
    emails: [email],
    tfp: 'B2C_1_loandepot_auth', // Policy name
  };

  const base64UrlEncode = (obj: object): string => {
    const json = JSON.stringify(obj);
    const base64 = btoa(json);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const fakeSignature = 'mock_signature_not_valid_for_production_use_only';

  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.${fakeSignature}`;
}

function generateMockAccessToken(): string {
  const header = {
    typ: 'JWT',
    alg: 'RS256',
    kid: 'mock-key-id',
  };

  const now = Math.floor(Date.now() / 1000);
  const payload = {
    iss: 'https://login-mock.loandepotdev.works/mock-tenant/v2.0/',
    sub: 'mock-user-id-12345',
    aud: 'mock-api-client-id',
    exp: now + 3600,
    iat: now,
    nbf: now,
    scp: 'impersonation',
  };

  const base64UrlEncode = (obj: object): string => {
    const json = JSON.stringify(obj);
    const base64 = btoa(json);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const fakeSignature = 'mock_access_token_signature';

  return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.${fakeSignature}`;
}

export const ropcAuthMockResponses: Record<string, RopcTokenResponse | { error: string; error_description: string }> = {
  success: {
    access_token: generateMockAccessToken(),
    token_type: 'Bearer',
    expires_in: 3600,
    id_token: generateMockIdToken('mock-user-id-12345', 'mockuser@example.com', 'Mock', 'User'),
    scope: 'openid profile offline_access',
    oid:''
  },

  'invalid-credentials': {
    error: 'invalid_grant',
    error_description: 'AADSTS50126: Invalid username or password. Please check your credentials and try again.',
  },

  'account-not-found': {
    error: 'invalid_grant',
    error_description: 'AADSTS50034: The user account does not exist in the directory. Please sign up first.',
  },

  'account-locked': {
    error: 'invalid_grant',
    error_description: 'AADSTS50053: Your account has been locked due to too many failed login attempts.',
  },

  'account-disabled': {
    error: 'invalid_grant',
    error_description: 'AADSTS50057: Your account has been disabled by an administrator.',
  },
};

export const mockAuthUser = {
  userId: 'mock-user-id-12345',
  email: 'mockuser@example.com',
  firstName: 'Mock',
  lastName: 'User',
};

export const mockAuthUserProfile = {
  'single-borrower': {
    userId: mockAuthUser.userId,
    email: mockAuthUser.email,
    firstName: mockAuthUser.firstName,
    lastName: mockAuthUser.lastName,
    sharedBorrowers: [
      { id: '1_BOR_aaa-0001_3000000001', firstName: 'Mock', lastName: 'User', verifyRequired: true },
    ],
  },
  'multiple-borrowers': {
    userId: mockAuthUser.userId,
    email: mockAuthUser.email,
    firstName: 'Christopher',
    lastName: 'Brown',
    sharedBorrowers: [
      { id: '1_BOR_93a60ac3-7944-4b97-9c00-59f44026177f_3000686861', firstName: 'Christopher', lastName: 'Brown', verifyRequired: true },
      { id: '1_BOR_b2c71d5e-8833-4a12-bc11-72e55037288a_3000686861', firstName: 'Amanda', lastName: 'Brown', verifyRequired: false },
    ],
  },
};

export const mlaAuthTokenMockResponses: Record<string, { success: boolean; data?: unknown; error?: string }> = {
  success: {
    success: true,
    data: {
      token: 'mock-mla-session-token-12345',
      userId: mockAuthUser.userId,
      visitorId: 'mock-visitor-id-67890',
      borrowers: [
        {
          borrowerId: 'mock-borrower-001',
          firstName: mockAuthUser.firstName,
          lastName: mockAuthUser.lastName,
          borrowerToken: 'mock-borrower-dashboard-token-abc',
        },
      ],
    },
  },

  'multiple-borrowers': {
    success: true,
    data: {
      token: 'mock-mla-session-token-multi',
      userId: mockAuthUser.userId,
      visitorId: 'mock-visitor-id-multi',
      borrowers: [
        {
          borrowerId: 'borrower-010a',
          firstName: 'Christopher',
          lastName: 'Brown',
          borrowerToken: 'mock-borrower-token-001',
          isPrimary: true,
          ssnVerify: true,
        },
        {
          borrowerId: 'borrower-010b',
          firstName: 'Amanda',
          lastName: 'Brown',
          borrowerToken: 'mock-borrower-token-002',
          isPrimary: false,
          ssnVerify: false,
        },
      ],
    },
  },

  'token-invalid': {
    success: false,
    error: 'Token validation failed. Invalid or expired B2C token.',
  },

  'user-not-found': {
    success: false,
    error: 'User not found. Please complete registration first.',
  },
};
